import { FilterCategoryType } from "@/utils/FiltersUtils";
import React, { ReactNode, createContext, useContext, useState } from "react";

interface FiltersContextType {
  selectedFilters: any[];
  setSelectedFilters: React.Dispatch<React.SetStateAction<any[]>>;
  showUnassignedOnly: boolean;
  setShowUnassignedOnly: React.Dispatch<React.SetStateAction<boolean>>;
  customerSearch: string;
  setCustomerSearch: React.Dispatch<React.SetStateAction<string>>;
  productSearch: string;
  setProductSearch: React.Dispatch<React.SetStateAction<string>>;
  addedFilterCategories: FilterCategoryType<null>[];
  setAddedFilterCategories: React.Dispatch<React.SetStateAction<FilterCategoryType<null>[]>>;
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  startDates: Date | null;
  setStartDates: React.Dispatch<React.SetStateAction<Date | null>>;
  endDates: Date | null;
  setEndDates: React.Dispatch<React.SetStateAction<Date | null>>;
  setRoute: React.Dispatch<React.SetStateAction<string>>;
  route: string;
  preparers: GenericNameAndExternalId[];
  setPreparers: React.Dispatch<React.SetStateAction<GenericNameAndExternalId[]>>;
  reviewers: GenericNameAndExternalId[];
  setReviewers: React.Dispatch<React.SetStateAction<GenericNameAndExternalId[]>>;
  showAllFlows: boolean;
  setShowAllFlows: React.Dispatch<React.SetStateAction<boolean>>;
  clearAllFilters: () => void;
}

interface GenericNameAndExternalId {
  name: string;
  id: string;
}

const FiltersContext = createContext<FiltersContextType | undefined>(undefined);

interface FiltersProviderProps {
  children: ReactNode;
}

export const FiltersProvider: React.FC<FiltersProviderProps> = ({ children }) => {
  //common states for filters
  const [selectedFilters, setSelectedFilters] = useState<any[]>([]);

  const [showUnassignedOnly, setShowUnassignedOnly] = useState<boolean>(false);
  const [customerSearch, setCustomerSearch] = useState<string>("");
  const [productSearch, setProductSearch] = useState<string>("");
  const [addedFilterCategories, setAddedFilterCategories] = useState<FilterCategoryType<null>[]>([]);

  //Product manager states
  const [startDates, setStartDates] = useState<Date | null>(null);
  const [endDates, setEndDates] = useState<Date | null>(null);
  const [preparers, setPreparers] = useState<GenericNameAndExternalId[]>([]);
  const [reviewers, setReviewers] = useState<GenericNameAndExternalId[]>([]);
  const [showAllFlows, setShowAllFlows] = useState<boolean>(true);

  const [route, setRoute] = useState<string>("");

  const [showFilters, setShowFilters] = useState<boolean>(false);

  const clearAllFilters = () => {
    setShowFilters(false);
    setSelectedFilters([]);
    setAddedFilterCategories([]);
  };

  const value: FiltersContextType = {
    selectedFilters,
    setSelectedFilters,
    showUnassignedOnly,
    setShowUnassignedOnly,
    customerSearch,
    setCustomerSearch,
    productSearch,
    setProductSearch,
    addedFilterCategories,
    setAddedFilterCategories,
    showFilters,
    setShowFilters,
    startDates,
    setStartDates,
    endDates,
    setEndDates,
    setRoute,
    route,
    preparers,
    setPreparers,
    reviewers,
    setReviewers,
    showAllFlows,
    setShowAllFlows,
    clearAllFilters,
  };

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
};

export function useFilters(): FiltersContextType {
  const context = useContext(FiltersContext);
  if (!context) {
    throw new Error("useFilters must be used within a FiltersProvider");
  }
  return context;
}
